import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, CircularProgress, TextField, Button, Snackbar, Alert, Link, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useParams } from "react-router-dom";
import { useBrandQuery, useUpdateVendorMeta, useGenerateBrandEmbeddingsFromUrl, useStartEmbeddingCrawl, useBrandEmbeddingsQuery } from "./hooks";
import BrandRatingSection from "./BrandRatingSection";
import BrandIngredientsSection from "./BrandIngredientsSection";

export default function BrandDetail() {
  const { id } = useParams();
  const [url, setUrl] = useState("");
  const { data, isLoading } = useBrandQuery(id);
  const { data: brandEmbeddings, isLoading: isEmbeddingsLoading } = useBrandEmbeddingsQuery(id);
  const updateVendorMeta = useUpdateVendorMeta(id);
  const startEmbeddingCrawl = useStartEmbeddingCrawl(id);
  const handleGenerateEmbedding = useGenerateBrandEmbeddingsFromUrl(id, url);
  const [vendorMeta, setVendorMeta] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (data?.brand?.Vendor?.VendorMeta) {
      const formFields = ["description", "websiteURL", "affiliateURL"];
      const filteredMeta = Object.fromEntries(
        Object.entries(data.brand.Vendor.VendorMeta)
          .filter(([key]) => formFields.includes(key))
      );
      setVendorMeta(filteredMeta);
    }
  }, [data]);

  const handleChange = (field) => (event) => {
    setVendorMeta({ ...vendorMeta, [field]: event.target.value });
  };

  const groupEmbeddingsByRootPath = () => {
    if (!brandEmbeddings?.embeddings) {
      return {};
    }

    const groups = {};

    brandEmbeddings.embeddings.forEach(embedding => {
      try {
        const urlObj = new URL(embedding.sourceUrl);
        const rootPath = `${urlObj.protocol}//${urlObj.hostname}`;
        if (!groups[rootPath]) {
          groups[rootPath] = [];
        }
        groups[rootPath].push(embedding);
      } catch (e) {
        if (!groups["Invalid URLs"]) {
          groups["Invalid URLs"] = [];
        }
        groups["Invalid URLs"].push(embedding);
      }
    });

    return groups;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await updateVendorMeta.mutateAsync(vendorMeta);
      setSnackbar({
        open: true,
        message: "Vendor meta updated successfully",
        severity: "success"
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update vendor meta",
        severity: "error"
      });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!data.brand) {
    return (
      <Box m={4}>
        <Typography variant="h2">Brand not found</Typography>
      </Box>
    );
  }

  return (
    <Box ml={-2} my={0}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h2" gutterBottom>
            {data.brand.Vendor.vendorName}
          </Typography>
          <Typography variant="h4" gutterBottom>
            <Link href={`/vendors/${data.brand.vendorId}`} style={{ textDecoration: "underline", color: "#1976d2" }}>
              Vendor ID: {data.brand.vendorId}
            </Link>
          </Typography>
          <Typography variant="h4" gutterBottom>
            <Link href={`https://brands.thecommons.earth/brand-rating/${data.brand.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", color: "#1976d2" }}>
              View brand on thecommons.earth
            </Link>
          </Typography>
          <Box mt={4}>
            <Typography variant="h3" gutterBottom>
              Brand Details
            </Typography>
            <Typography>Brand ID: {data.brand.id}</Typography>
            <Typography>Sustainability Status: {data.brand.sustainabilityStatus}</Typography>
            <Typography>Review Status: {data.brand.reviewStatus}</Typography>
            <Typography>Rubric Score: {data.brand.rubricScore?.score}</Typography>
            <Typography>
              Published Date: {data.brand.firstPublishedAt ? new Date(data.brand.firstPublishedAt).toLocaleDateString() : "-"}
            </Typography>
          </Box>
          <Box mt={4}>
            <Typography variant="h3" gutterBottom>
              Vendor Meta
            </Typography>
            <form onSubmit={handleSubmit}>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Website URL"
                  value={vendorMeta.websiteURL || ""}
                  onChange={handleChange("websiteURL")}
                  fullWidth
                  sx={{ maxWidth: 500 }}
                />
                <TextField
                  label="Affiliate URL"
                  value={vendorMeta.affiliateURL || ""}
                  onChange={handleChange("affiliateURL")}
                  fullWidth
                  sx={{ maxWidth: 500 }}
                />
                <TextField
                  label="Description"
                  value={vendorMeta.description || ""}
                  onChange={handleChange("description")}
                  fullWidth
                  multiline
                  rows={4}
                  sx={{ maxWidth: 500 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={updateVendorMeta.isPending}
                  sx={{ width: "fit-content" }}
                >
                  {updateVendorMeta.isPending ? "Updating..." : "Update"}
                </Button>
              </Box>
            </form>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <BrandRatingSection brandId={id} />

          <Typography variant="h3" gutterBottom>
            Scraped Data
          </Typography>
          <Box mt={4}>
            <Box mb={3}>
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                  label="URL"
                  placeholder="https://example.com/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="contained"
                  disabled={!url || handleGenerateEmbedding.isPending}
                  onClick={async () => {
                    try {
                      await handleGenerateEmbedding.mutateAsync();
                      setSnackbar({
                        open: true,
                        message: "Embeddings generated successfully",
                        severity: "success"
                      });
                      setUrl(""); // Clear the URL field after success
                    } catch (error) {
                      setSnackbar({
                        open: true,
                        message: error.message || "Failed to generate embeddings",
                        severity: "error"
                      });
                    }
                  }}
                >
                  {handleGenerateEmbedding.isPending ? "Scraping..." : "Scrape Specific URL"}
                </Button>
              </Box>
              <Button
                variant="contained"
                color="primary"
                disabled={!data?.brand?.Vendor?.VendorMeta?.websiteURL || startEmbeddingCrawl.isPending}
                onClick={async () => {
                  try {
                    await startEmbeddingCrawl.mutateAsync();
                    setSnackbar({
                      open: true,
                      message: "Scraping job scheduled successfully",
                      severity: "success"
                    });
                  } catch (error) {
                    setSnackbar({
                      open: true,
                      message: error.message || "Failed to schedule scraping job",
                      severity: "error"
                    });
                  }
                }}
                fullWidth
              >
                {startEmbeddingCrawl.isPending ? "Scheduling..." : "Scrape Brand Website Pages"}
              </Button>
            </Box>
            {isEmbeddingsLoading
              ? (
                <Box mb={2}>
                  <Typography>Loading embeddings...</Typography>
                </Box>
              )
              : brandEmbeddings?.embeddings?.length > 0
                ? (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Embedded Pages ({brandEmbeddings.embeddings.length} chunks from {[...new Set(brandEmbeddings.embeddings.map(e => e.sourceUrl))].length} unique pages)
                    </Typography>
                    <Box>
                      {Object.entries(groupEmbeddingsByRootPath()).map(([rootPath, embeddings]) => (
                        <Accordion key={rootPath} defaultExpanded={Object.keys(groupEmbeddingsByRootPath()).length === 1}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                              {rootPath} ({[...new Set(embeddings.map(e => e.sourceUrl))].length} {[...new Set(embeddings.map(e => e.sourceUrl))].length === 1 ? "page" : "pages"}, {embeddings.length} {embeddings.length === 1 ? "chunk" : "chunks"})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box component="ul" sx={{ pl: 2, m: 0 }}>
                              {embeddings.sort((a, b) => a.sourceUrl.localeCompare(b.sourceUrl) || a.chunkIndex - b.chunkIndex).map((embedding, index) => (
                                <li key={index} style={{ marginBottom: "8px" }}>
                                  <Link href={embedding.sourceUrl} target="_blank" rel="noopener noreferrer">
                                    {embedding.sourceUrl}
                                  </Link>
                                  {" "}
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    (chunk {embedding.chunkIndex})
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic", color: "text.secondary" }}>
                                    {embedding.content}...
                                  </Typography>
                                </li>
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  </>
                )
                : (
                  <></>
                )}
          </Box>

          <BrandIngredientsSection brandId={id} />
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
